import React, {
    useState,
    useCallback,
    useEffect,
    forwardRef,
    useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';

import {
    AdornedInput,
    Adornment,
    Form,
    IconCloseCircle,
    IconSearch,
    Input,
    Spinner,
} from '@zillow/constellation';

const propTypes = {
    initialText: PropTypes.string,
    placeholder: PropTypes.string,
    onSearch: PropTypes.func,
    onSubmit: PropTypes.func,
    isSearching: PropTypes.bool,
    iconSize: PropTypes.string,
    debounceMilliseconds: PropTypes.number,
};

const defaultProps = {
    initialText: '',
    placeholder: 'Search',
    onSubmit: () => { },
    onSearch: () => { },
    isSearching: false,
    iconSize: 'xs',
    debounceMilliseconds: 200,
};

const SearchBox = forwardRef(({
    initialText,
    onSearch,
    onSubmit,
    iconSize: size,
    placeholder,
    isSearching,
    debounceMilliseconds,
}, ref) => {
    const [searchText, setSearchText] = useState(initialText);

    const handleSearchDebounced = useCallback(debounce(onSearch, debounceMilliseconds), []);

    useEffect(() => {
        handleSearchDebounced(searchText);

        return () => handleSearchDebounced.cancel;
    }, [searchText, handleSearchDebounced]);

    const handleChange = (e) => {
        setSearchText(e.target.value);
    };

    const clearSearchText = () => {
        setSearchText('');
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (onSubmit) onSubmit(searchText);
    };

    const handleKeyUp = (e) => {
        if (e.key === 'Escape') clearSearchText();
    };

    const renderSpinnerOrClear = () => (
        searchText ? (
            <IconCloseCircle
                size={size}
                onClick={clearSearchText}
            />
        ) : <IconSearch size={size} />
    );

    /* ACKNOWLEDGEMENT
     * ref: https://stackoverflow.com/a/37950970
     * parent component requires this to be able to ref the child function
     */
    useImperativeHandle(ref, () => ({
        updateSearchText: (newText = defaultProps.initialText) => {
            setSearchText(newText);
        },
    }));

    return (
        <Form onSubmit={handleSubmit}>
            <AdornedInput
                data-test="SearchBox-Component"
                input={(
                    <Input
                        placeholder={placeholder}
                        onChange={handleChange}
                        onKeyUp={handleKeyUp}
                        value={searchText}
                    />
                )}
                rightAdornment={(
                    <Adornment>
                        {isSearching ? <Spinner size={size} /> : renderSpinnerOrClear()}
                    </Adornment>
                )}
            />
        </Form>
    );
});

SearchBox.propTypes = propTypes;
SearchBox.defaultProps = defaultProps;

export { SearchBox };
export default SearchBox;
